/** Game mode / lobby type vocabulary and match-eligibility rules. */
export const GAME_MODES = {
    0: 'Unknown',
    1: 'All Pick',
    2: "Captain's Mode",
    3: 'Random Draft',
    4: 'Single Draft',
    5: 'All Random',
    6: 'Intro',
    7: 'Diretide',
    8: 'Reverse Captains Mode',
    9: 'Greeviling',
    10: 'Tutorial',
    11: 'Mid Only',
    12: 'Least Played',
    13: 'Limited Heroes',
    14: 'Compendium Matchmaking',
    15: 'Custom',
    16: "Captain's Draft",
    17: 'Balanced Draft',
    18: 'Ability Draft',
    19: 'Event',
    20: 'All Random Deathmatch',
    21: '1v1 Mid',
    22: 'All Draft',
    23: 'Turbo',
    24: 'Mutation',
};
export const LOBBY_TYPES = {
    0: 'Normal',
    1: 'Practice',
    2: 'Tournament',
    3: 'Tutorial',
    4: 'Co-op vs Bots',
    5: 'Ranked Team MM',
    6: 'Ranked Solo MM',
    7: 'Ranked',
    8: '1v1 Mid',
    9: 'Battle Cup',
    10: 'Local Bots',
    11: 'Spectator',
    12: 'Event',
};
export const LOBBY_RANKED = 7;
export const LOBBY_TOURNAMENT = 2;
export const GAME_MODE_TURBO = 23;
export const GAME_MODE_ABILITY_DRAFT = 18;
export const GAME_MODE_1V1_MID = 21;
export const GAME_MODE_ALL_RANDOM_DEATHMATCH = 20;
/**
 * Game modes that cannot be scored against standard benchmarks.
 *
 * Every number this app produces compares a player to others on the SAME HERO
 * in a standard 5v5 game. A mode that changes the economy, the pacing, or what
 * a hero even is breaks that comparison, so those games are excluded rather
 * than averaged in — a Turbo game's doubled gold would read as a career-best
 * farming performance.
 *
 * Ability Draft is the clearest case and the reason this list grew: your hero
 * carries four abilities drafted from a shared pool, so "an average Lina"
 * describes nothing about the hero you actually played. Scoring it against Lina
 * benchmarks produces a confident number that means nothing.
 *
 * Event modes (Diretide, Greeviling, Mutation, and whatever Valve labels 19)
 * change objectives and item economies outright. Mid Only and 1v1 are not five
 * versus five, so team-relative metrics like kill participation have no
 * denominator.
 *
 * NOT excluded, deliberately: All Random, Single/Random/Balanced/Captain's
 * Draft and Least Played. Those restrict how you obtain a hero, not how the
 * hero plays, so per-hero benchmarks remain exactly as meaningful.
 *
 * Exported as an array as well as a Set because the ingest layer builds an
 * equivalent SQL predicate from it. Restating these ids in SQL would be a second
 * copy of the rule, free to drift from `isAnalyzable` without anything failing.
 */
export const NON_STANDARD_GAME_MODES = [
    GAME_MODE_TURBO,
    GAME_MODE_1V1_MID,
    GAME_MODE_ALL_RANDOM_DEATHMATCH,
    GAME_MODE_ABILITY_DRAFT,
    15, // Custom
    10, // Tutorial
    6, // Intro
    7, // Diretide
    9, // Greeviling
    11, // Mid Only
    14, // Compendium Matchmaking
    19, // Event
    24, // Mutation
];
const NON_STANDARD_MODES = new Set(NON_STANDARD_GAME_MODES);
/**
 * Lobby types that are not real games played against humans.
 *
 * Note what is deliberately NOT here: lobby type 1 ("Practice"). That covers
 * private lobbies, which for serious players are scrims — full-length drafted
 * games against real opponents and some of the most informative data they
 * have. Excluding them discards the bulk of a competitive player's history.
 * Genuinely low-value practice lobbies (item testing, messing about) are short
 * and are already removed by the duration floor.
 */
export const NON_COMPETITIVE_LOBBY_TYPES = [
    3, // Tutorial
    4, // Co-op vs Bots
    10, // Local Bots
    11, // Spectator
];
const NON_COMPETITIVE_LOBBIES = new Set(NON_COMPETITIVE_LOBBY_TYPES);
export function gameModeName(mode) {
    if (mode == null)
        return 'Unknown';
    return GAME_MODES[mode] ?? `Mode ${mode}`;
}
export function lobbyTypeName(lobby) {
    if (lobby == null)
        return 'Unknown';
    return LOBBY_TYPES[lobby] ?? `Lobby ${lobby}`;
}
export function isRanked(lobbyType) {
    return lobbyType === LOBBY_RANKED;
}
export function isTurbo(gameMode) {
    return gameMode === GAME_MODE_TURBO;
}
/** Minimum duration (seconds) for a match to carry meaningful signal. */
export const MIN_ANALYZABLE_DURATION = 600; // 10 minutes
/** leaver_status: 0 = none, 1 = disconnected (reconnected), 2+ = abandoned. */
export const MIN_ABANDON_LEAVER_STATUS = 2;
/**
 * Whether a player was in the game at all, whatever the leaver status says.
 *
 * `leaver_status` 1 is documented as "disconnected but reconnected", so the
 * abandon threshold correctly lets it through — someone who dropped for thirty
 * seconds played the game. But it also covers players who dropped at the horn
 * and never came back, and those rows are indistinguishable by status alone.
 *
 * Zero hero damage AND zero last hits over a full-length game is the tell, and
 * it is not a judgement about playing badly: it is physically impossible to
 * spend ten minutes in a Dota game and record neither. Three such rows on one
 * league profile showed level 0 and level 1 after 48 and 55 minutes, scoring
 * zero and dragging the player's average down for games they never joined.
 *
 * Both fields null means a summary-only row that never had them, which is not
 * evidence of anything — so those are left alone.
 */
export function didNotParticipate(player) {
    if (player.leaverStatus == null || player.leaverStatus <= 0)
        return false;
    if (player.heroDamage == null || player.lastHits == null)
        return false;
    return player.heroDamage === 0 && player.lastHits === 0;
}
/**
 * Why this match is excluded, or null if it is analysable.
 *
 * Shares its rules with `isAnalyzable` by construction — that function is
 * defined in terms of this one, so the two cannot disagree about which matches
 * count while disagreeing about why.
 */
export function exclusionReason(match) {
    if (match.duration != null && match.duration < MIN_ANALYZABLE_DURATION)
        return 'too_short';
    if (match.gameMode != null && NON_STANDARD_MODES.has(match.gameMode))
        return 'non_standard_mode';
    if (match.lobbyType != null && NON_COMPETITIVE_LOBBIES.has(match.lobbyType)) {
        return 'not_a_real_game';
    }
    if (didNotParticipate(match))
        return 'did_not_play';
    if (match.leaverStatus != null && match.leaverStatus >= MIN_ABANDON_LEAVER_STATUS) {
        return 'abandoned';
    }
    return null;
}
export function isAnalyzable(match) {
    return exclusionReason(match) === null;
}
/** Valve encodes team membership in `player_slot`: 0-4 Radiant, 128-132 Dire. */
export function isRadiantSlot(playerSlot) {
    return playerSlot < 128;
}
export function didWin(playerSlot, radiantWin) {
    return isRadiantSlot(playerSlot) === radiantWin;
}
/** Position of the player within their team (0-4). */
export function teamSlot(playerSlot) {
    return isRadiantSlot(playerSlot) ? playerSlot : playerSlot - 128;
}
//# sourceMappingURL=game.js.map